<template>
  <div>
    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary" class="mr-2"></b-spinner>
      <span class="font-weight-bold">Loading fee details...</span>
    </div>
    <div v-else>
      <b-card class="due-summary-card mb-3" :class="dueAmount > 0 ? 'border-danger' : 'border-success'">
        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <h6 class="text-uppercase text-muted mb-1">Total Payment Due</h6>
            <h3 class="mb-0" :class="dueAmount > 0 ? 'text-danger' : 'text-success'">
              &#8377;{{ formatAmount(dueAmount) }}
            </h3>
          </div>
          <div class="d-flex align-items-center">
            <b-button v-if="dueAmount > 0" variant="success" class="mr-3" @click="openPayModal">
              Pay Now
            </b-button>
            <i class="fas fa-coins fa-3x" :class="dueAmount > 0 ? 'text-danger' : 'text-success'"></i>
          </div>
        </div>
      </b-card>

      <div v-if="pendingComponents.length > 0" class="mb-4">
        <div class="d-flex justify-content-between align-items-center border-bottom pb-2">
          <h5 class="text-uppercase text-info font-weight-bold mb-0">
            <i class="fas fa-list mr-2" style="font-size: 14px !important;"></i>Pending Fee Components
            <small v-if="feeStructureName" class="text-muted font-weight-normal text-lowercase">({{ feeStructureName }})</small>
          </h5>
          <label class="mb-0">
            <input type="checkbox" v-model="selectAllComponents" :disabled="!payableComponents.length" />
            Select All
          </label>
        </div>
        <p class="text-muted mb-2">Select which bill(s) you'd like to pay.</p>

        <div v-for="group in componentGroups" :key="group.component_id" class="mt-2">
          <h6 class="font-weight-bold mb-1" style="font-size: 14px !important;">{{ group.component }}</h6>
          <div class="table-responsive"  style="height: auto !important;">
            <table class="table table-sm table-bordered mb-0" style="table-layout: fixed;">
              <colgroup>
                <col style="width: 5%;" />
                <col style="width: 30%;" />
                <col style="width: 15%;" />
                <col style="width: 15%;" />
                <col style="width: 15%;" />
                <col style="width: 20%;" />
              </colgroup>
              <thead class="thead-light">
                <tr>
                  <th></th>
                  <th>Bill</th>
                  <th class="text-right">Demand</th>
                  <th class="text-right">Credit</th>
                  <th class="text-right">Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in group.rows" :key="row.group_id">
                  <td>
                    <input
                      type="checkbox"
                      v-model="selectedGroups[row.group_id]"
                      :disabled="row.balance <= 0"
                    />
                  </td>
                  <td>
                    <template v-if="row.bill_name">
                      {{ row.bill_name }}
                      <small class="text-muted d-block">{{ row.bill_number }}</small>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-right">&#8377;{{ formatAmount(row.amount) }}</td>
                  <td class="text-right">&#8377;{{ formatAmount(row.credit) }}</td>
                  <td class="text-right">&#8377;{{ formatAmount(row.balance) }}</td>
                  <td>
                    <b-badge :variant="rowStatusVariant(row)">{{ rowStatus(row) }}</b-badge>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="font-weight-bold" style="background-color: #e9ecef;">
                  <td colspan="2">Total</td>
                  <td class="text-right">&#8377;{{ formatAmount(groupTotal(group, 'amount')) }}</td>
                  <td class="text-right">&#8377;{{ formatAmount(groupTotal(group, 'credit')) }}</td>
                  <td class="text-right">&#8377;{{ formatAmount(groupTotal(group, 'balance')) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="alert alert-secondary d-flex justify-content-between font-weight-bold mt-4 mb-0">
          <span>Grand Total</span>
          <span>
            Total: &#8377;{{ formatAmount(pendingTotalAmount) }}
            &nbsp;|&nbsp; Paid: &#8377;{{ formatAmount(pendingTotalCredit) }}
            &nbsp;|&nbsp; Unpaid: &#8377;{{ formatAmount(pendingTotalBalance) }}
          </span>
        </div>
      </div>

      <div>
        <h5 class="text-uppercase text-info font-weight-bold">
          <i class="fas fa-history mr-2" style="font-size: 14px !important;"></i>Transaction History
        </h5>
        <table v-if="transactions.length > 0" class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <tr>
              <th>Date</th>
              <th class="text-right">Amount Paid</th>
              <th>Payment Mode</th>
              <th class="text-right">Balance</th>
              <th>Collected By</th>
              <th>Receipt</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(transaction, index) in transactions" :key="index">
              <td>{{ formatDate(transaction.collected_date) }}</td>
              <td class="text-right">&#8377;{{ formatAmount(transaction.fee_collected) }}</td>
              <td>{{ paymentModeText(transaction.payment_mode) }}</td>
              <td class="text-right">&#8377;{{ formatAmount(transaction.balance_amount) }}</td>
              <td>
                <template v-if="transaction.collected_by">
                  {{ transaction.collected_by.first_name }} {{ transaction.collected_by.last_name }}
                </template>
              </td>
              <td>
                <a href="#" class="small" @click.prevent="downloadReceipt(transaction.id)">
                  <i class="fas fa-file-pdf mr-1"></i>Download
                </a>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted">No transactions found.</p>
      </div>
    </div>

    <!-- Pay Now Modal -->
    <b-modal v-model="showPayModal" title="Pay Fee" centered hide-footer @hidden="resetPayModal">
      <div class="mb-3">
        <label class="font-weight-bold d-block">Amount Due for Selected Bill(s):</label>
        <span class="text-danger font-weight-bold">&#8377;{{ formatAmount(selectedBalance) }}</span>
      </div>

      <div class="mb-3" v-if="selectedComponents.length">
        <label class="font-weight-bold d-block">Enter amount to pay for each bill:</label>
        <div v-for="c in selectedComponents" :key="c.group_id" class="mb-3 pb-2 border-bottom">
          <div class="d-flex justify-content-between">
            <span>
              {{ c.component }}
              <small v-if="c.bill_name" class="text-muted d-block">{{ c.bill_name }} ({{ c.bill_number }})</small>
            </span>
            <span class="font-weight-bold">Balance: &#8377;{{ formatAmount(c.balance) }}</span>
          </div>

          <ul v-if="c.breakdown && c.breakdown.length > 1" class="list-unstyled mb-1 mt-1">
            <li
              v-for="(item, i) in c.breakdown"
              :key="i"
              class="d-flex justify-content-between"
              :class="item.is_adjustment ? (item.amount < 0 ? 'text-success' : 'text-danger') : 'text-muted'"
            >
              <small>{{ item.is_adjustment ? (item.note || 'Adjustment') : 'Base' }}</small>
              <small>{{ item.is_adjustment && item.amount > 0 ? '+' : '' }}&#8377;{{ formatAmount(item.amount) }}</small>
            </li>
          </ul>

          <input
            type="number"
            class="form-control mt-1"
            min="0"
            :max="c.balance"
            v-model.number="payAmounts[c.group_id]"
            :disabled="!c.allow_partial_payment"
          />
          <small v-if="!c.allow_partial_payment" class="text-muted">
            Partial payment isn't allowed for this bill - full amount only.
          </small>
          <small class="text-danger" v-if="payAmountErrors[c.group_id]">
            {{ payAmountErrors[c.group_id] }}
          </small>
        </div>

        <div class="d-flex justify-content-between font-weight-bold pt-2 border-top">
          <span>Total to Pay:</span>
          <span>&#8377;{{ formatAmount(totalPayAmount) }}</span>
        </div>
      </div>

      <div v-if="requiresConsent" class="form-check mb-3">
        <input
          id="policy-consent-check"
          type="checkbox"
          class="form-check-input"
          v-model="agreedToPolicies"
        />
        <span class="form-check-label">
          I agree to the
          <template v-for="policy in policiesForConsent">
            <a :key="policy.key" href="javascript:void(0)" @click.prevent="openPolicyInNewTab(policy)">{{ policy.label }}</a>{{ policy.separator }}
          </template>
        </span>
      </div>

      <div class="text-center">
        <b-button variant="outline-secondary" class="mr-2" @click="showPayModal = false">
          Cancel
        </b-button>
        <b-button
          variant="success"
          :disabled="checkoutLoading || (requiresConsent && !agreedToPolicies)"
          @click="proceedToPayment"
        >
          {{ checkoutLoading ? 'Please wait…' : 'Proceed to Payment' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Payment Response Modal - shown right after returning from the gateway.
         Order Number and Amount here come from our own status() endpoint,
         which re-confirms with HDFC's Status API - never from the redirect
         itself - so what's shown always matches what HDFC actually recorded. -->
    <b-modal v-model="showPaymentStatusModal" :title="paymentStatusTitle" centered hide-footer>
      <div class="text-center py-3">
        <i :class="paymentStatusIcon" style="font-size: 48px;"></i>
        <h5 class="mt-3 mb-0">{{ paymentStatusMessage }}</h5>

        <div class="mt-4 text-left" v-if="paymentStatusResult.orderId">
          <div class="d-flex justify-content-between border-bottom py-2">
            <span class="text-muted">Order Number</span>
            <span class="font-weight-bold">{{ paymentStatusResult.orderId }}</span>
          </div>
          <div class="d-flex justify-content-between py-2">
            <span class="text-muted">Amount</span>
            <span class="font-weight-bold">&#8377;{{ formatAmount(paymentStatusResult.amount) }}</span>
          </div>
        </div>
      </div>

      <div class="text-center mt-2">
        <b-button variant="primary" @click="showPaymentStatusModal = false">Close</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      dueAmount: 0,
      ledgers: [],
      pendingComponents: [],
      selectedGroups: {},

      showPayModal: false,
      payAmounts: {},
      payAmountErrors: {},
      checkoutLoading: false,

      policies: null,
      agreedToPolicies: false,

      showPaymentStatusModal: false,
      paymentStatusResult: { orderId: null, amount: 0, status: null },
    };
  },
  computed: {
    storeInstitutionId() {
      return this.$store.getters.getCurrentInstitution.id;
    },
    availablePolicies() {
      if (!this.policies) return [];
      const policyTypes = [
        { key: 'terms_and_conditions', label: 'Terms & Conditions' },
        { key: 'privacy_policy', label: 'Privacy Policy' },
        { key: 'cancellation_refund_policy', label: 'Cancellation / Refund Policy' },
      ];
      return policyTypes
        .filter((policy) => this.policies[policy.key])
        .map((policy) => ({ ...policy, content: this.policies[policy.key] }));
    },
    requiresConsent() {
      return this.availablePolicies.length > 0;
    },
    payableComponents() {
      return this.pendingComponents.filter((c) => c.balance > 0);
    },
    selectAllComponents: {
      get() {
        return this.payableComponents.length > 0
          && this.payableComponents.every((c) => this.selectedGroups[c.group_id]);
      },
      set(value) {
        this.payableComponents.forEach((c) => {
          this.$set(this.selectedGroups, c.group_id, value);
        });
      },
    },
    selectedComponents() {
      return this.payableComponents.filter((c) => this.selectedGroups[c.group_id]);
    },
    selectedBalance() {
      return this.selectedComponents.reduce((s, c) => s + c.balance, 0);
    },
    totalPayAmount() {
      return this.selectedComponents.reduce(
        (s, c) => s + (Number(this.payAmounts[c.group_id]) || 0),
        0
      );
    },
    feeStructureName() {
      return this.pendingComponents.length ? this.pendingComponents[0].category : '';
    },
    // A component can have multiple bills - group its rows together so they
    // list under one component heading, mirroring the staff Collect Fee page.
    componentGroups() {
      const map = {};
      const order = [];
      this.pendingComponents.forEach((row) => {
        const key = row.fee_component_id;
        if (!map[key]) {
          map[key] = { component_id: key, component: row.component, rows: [] };
          order.push(key);
        }
        map[key].rows.push(row);
      });
      return order.map((key) => map[key]);
    },
    pendingTotalAmount() {
      return this.pendingComponents.reduce((s, c) => s + Number(c.amount || 0), 0);
    },
    pendingTotalCredit() {
      return this.pendingComponents.reduce((s, c) => s + Number(c.credit || 0), 0);
    },
    pendingTotalBalance() {
      return this.pendingComponents.reduce((s, c) => s + Number(c.balance || 0), 0);
    },
    policiesForConsent() {
      const policies = this.availablePolicies;
      return policies.map((policy, index) => {
        let separator = '';
        if (index < policies.length - 2) separator = ', ';
        else if (index === policies.length - 2) separator = ' and ';
        return { ...policy, separator };
      });
    },
    transactions() {
      const collections = [];
      this.ledgers.forEach((ledger) => {
        (ledger.fee_collections || []).forEach((collection) => {
          collections.push(collection);
        });
      });
      return collections.sort(
        (a, b) => new Date(b.collected_date) - new Date(a.collected_date)
      );
    },
    paymentStatusTitle() {
      const status = this.paymentStatusResult.status;
      if (status === 'paid') return 'Payment Successful';
      if (status === 'failed') return 'Payment Failed';
      return 'Payment Processing';
    },
    paymentStatusMessage() {
      const status = this.paymentStatusResult.status;
      if (status === 'paid') return 'Your payment was received successfully.';
      if (status === 'failed') return 'Your payment could not be completed. Please try again.';
      return 'Your payment is still processing. Please check back shortly.';
    },
    paymentStatusIcon() {
      const status = this.paymentStatusResult.status;
      if (status === 'paid') return 'fas fa-check-circle text-success';
      if (status === 'failed') return 'fas fa-times-circle text-danger';
      return 'fas fa-clock text-warning';
    },
  },
  async created() {
    await Promise.all([this.getMyFeeLedger(), this.getInstitutionPolicies()]);
    await this.checkPaymentReturn();
  },
  watch: {
    storeInstitutionId(institutionId) {
      if (institutionId && this.policies === null) {
        this.getInstitutionPolicies();
      }
    },
  },
  methods: {
    async getMyFeeLedger() {
      this.loading = true;
      const url = this.$store.getters.getAPIKey.mainAPI + `/studentcategorymappings/my-ledger`;
      await this.$axios
        .get(url)
        .then((response) => {
          this.ledgers = response.data.ledgers;
          this.dueAmount = response.data.due_amount;
          this.pendingComponents = response.data.pending_components;
          // Nothing pre-selected - student picks which bill(s) to pay.
          this.selectedGroups = this.pendingComponents
            .reduce((a, c) => ({ ...a, [c.group_id]: false }), {});
        })
        .catch(() => {});
      this.loading = false;
    },
    async getInstitutionPolicies() {
      const institutionId = this.storeInstitutionId;
      if (!institutionId) return;
      const url = this.$store.getters.getAPIKey.mainAPI + `/institutions/${institutionId}/policies`;
      await this.$axios
        .get(url)
        .then((response) => {
          this.policies = response.data.policies;
        })
        .catch(() => {});
    },
    openPolicyInNewTab(policy) {
      const route = this.$router.resolve({
        name: 'institutionPolicy',
        params: { institutionId: this.storeInstitutionId, type: policy.key },
      });
      window.open(route.href, '_blank');
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-GB');
    },
    paymentModeText(mode) {
      const modes = {
        1: 'Cash',
        2: 'Bank Transfer',
        3: 'Cheque',
        4: 'DD',
        5: 'POS',
        6: 'RTGS',
        7: 'NEFT',
        8: 'UPI',
        9: 'Bank Loan',
        10: 'Wallet',
        11: 'Online',
      };
      return modes[mode] || '';
    },
    async downloadReceipt(feeCollectionId) {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/feecollections/${feeCollectionId}/receipt`;
      try {
        const response = await this.$axios.get(url, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `Receipt-${feeCollectionId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        this.$toast.error('Failed to download receipt. Please try again.');
      }
    },
    groupTotal(group, key) {
      return group.rows.reduce((s, row) => s + Number(row[key] || 0), 0);
    },
    rowStatus(row) {
      if (row.balance <= 0.0001) return 'Paid';
      if (row.credit > 0) return 'Partially Paid';
      return 'Unpaid';
    },
    rowStatusVariant(row) {
      const status = this.rowStatus(row);
      if (status === 'Paid') return 'success';
      if (status === 'Partially Paid') return 'warning';
      return 'danger';
    },
    openPayModal() {
      if (!this.selectedBalance) {
        this.$toast.error('Please select at least one bill to pay.');
        return;
      }
      this.payAmounts = this.selectedComponents.reduce(
        (a, c) => ({ ...a, [c.group_id]: c.balance }),
        {}
      );
      this.payAmountErrors = {};
      this.agreedToPolicies = false;
      this.showPayModal = true;
    },
    resetPayModal() {
      this.payAmounts = {};
      this.payAmountErrors = {};
      this.agreedToPolicies = false;
    },
    async proceedToPayment() {
      const errors = {};

      this.selectedComponents.forEach((c) => {
        const amount = Number(this.payAmounts[c.group_id]);
        if (!amount || amount <= 0) {
          errors[c.group_id] = 'Please enter a valid amount.';
        } else if (amount > c.balance) {
          errors[c.group_id] = 'Amount cannot exceed the balance for this bill.';
        } else if (!c.allow_partial_payment && amount !== c.balance) {
          errors[c.group_id] = 'Partial payment isn\'t allowed - full amount required.';
        }
      });

      this.payAmountErrors = errors;
      if (Object.keys(errors).length > 0) {
        return;
      }
      if (this.requiresConsent && !this.agreedToPolicies) {
        return;
      }

      this.checkoutLoading = true;
      const mappingId = this.selectedComponents[0].fee_student_category_mapping_id;
      const allocations = this.selectedComponents.map((c) => ({
        group_id: c.group_id,
        amount: Number(this.payAmounts[c.group_id]),
      }));

      const url = this.$store.getters.getAPIKey.mainAPI + '/studentcategorymappings/pay-online/create-order';
      await this.$axios
        .post(url, { fee_student_category_mapping_id: mappingId, allocations })
        .then((response) => {
          // Razorpay's hosted checkout is reached by POSTing a form rather than
          // by following a URL. Either way the browser leaves this page and
          // comes back to the return route, so checkoutLoading stays set.
          if (response.data.checkout) {
            this.submitHostedCheckout(response.data.checkout);
            return null;
          }

          // Full-page redirect to Juspay's hosted checkout - it sends the
          // browser back to JUSPAY_RETURN_URL with ?order_id=... afterwards.
          window.location.href = response.data.checkout_url;
          return null;
        })
        .catch((error) => {
          this.checkoutLoading = false;
          this.$toast.error(
            (error.response && error.response.data && error.response.data.message)
              || 'Unable to start payment. Please try again.'
          );
        });
    },
    // HDFC CollectNow mandates Razorpay's hosted (embedded) checkout rather than
    // the Standard Checkout modal, so there is no script to load and no modal to
    // manage: we build the form Razorpay documents and submit it, which navigates
    // the browser away exactly as the SmartGateway flow does. Razorpay then posts
    // the payer back to callback_url, which is the same return route.
    submitHostedCheckout(checkout) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = checkout.action;
      // Nothing on this page should outlive the navigation, but keep it out of
      // the layout anyway in case the POST is blocked and we stay put.
      form.style.display = 'none';

      // fields is a flat name => value map of exactly the hidden inputs to
      // render, including Razorpay's bracketed names like prefill[email].
      Object.keys(checkout.fields || {}).forEach((name) => {
        const value = checkout.fields[name];
        if (value === null || value === undefined || value === '') return;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    },
    async checkPaymentReturn() {
      const orderId = this.$route.query.order_id || this.getReturnedOrderId();
      if (!orderId) return;

      await this.confirmPayment(orderId);

      // Drop order_id from the URL so a page refresh doesn't re-trigger this check.
      this.$router.replace({ query: {} }).catch(() => {});
      this.stripReturnedOrderId();
    },
    // Shared by both hand-over styles: the redirect return (SmartGateway) and
    // the in-page checkout's handler (Razorpay).
    async confirmPayment(orderId) {
      const url = this.$store.getters.getAPIKey.mainAPI + `/studentcategorymappings/pay-online/status/${orderId}`;
      await this.$axios
        .get(url)
        .then((response) => {
          // order_id/amount/status here come straight from our own record,
          // which confirmOrder() only ever sets after re-checking HDFC's own
          // Status API - never from whatever the browser redirect claimed.
          this.paymentStatusResult = {
            orderId: response.data.order_id,
            amount: response.data.amount,
            status: response.data.status,
          };
          this.showPaymentStatusModal = true;

          if (response.data.status === 'paid') {
            return this.getMyFeeLedger();
          }
          return null;
        })
        .catch(() => {});
    },
    // The app uses hash-mode routing, so $route.query only sees params that
    // live inside the '#'. The gateway's redirect doesn't always put
    // order_id there - it sometimes appends it to the real URL query string
    // (before the '#'), which hash-mode routing never parses. Fall back to
    // reading it straight off window.location for that case.
    getReturnedOrderId() {
      return new URLSearchParams(window.location.search).get('order_id');
    },
    stripReturnedOrderId() {
      if (!window.location.search) return;
      const params = new URLSearchParams(window.location.search);
      params.delete('order_id');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState(null, '', newUrl);
    },
  },
};
</script>

<style scoped>
.due-summary-card {
  background-color: #fff;
}
</style>
